import {
  Award,
  Headphones,
  Leaf,
  Package,
  Palette,
  Ruler,
  ShieldCheck,
  Truck,
  Undo2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export const siteContent = {
  brand: {
    name: "Tilefort",
    tagline: "Tiles for every room, built to last.",
    phone: "0800 123 4567",
    email: "hello@tilefort.co.uk",
    address: "Unit 12, Wharfside Way, Leeds LS10 1PE",
  },
  hero: {
    eyebrow: "New season",
    title: "Tiles that carry\na room's character.",
    description:
      "Porcelain, marble, mosaic and outdoor — handpicked from European makers, ready to sample and delivered UK-wide.",
    primaryCta: { label: "Shop collections", href: "/collections" },
    secondaryCta: { label: "Order free samples", href: "/contact" },
    image: {
      url: "https://picsum.photos/seed/tilefort-hero/1400/1100",
      altText: "Warm terracotta-toned tiled interior",
    },
  },
  heroSlides: [
    {
      id: "slide-1",
      eyebrow: "New season",
      title: "Design starts with the right tile.",
      description:
        "Handpicked porcelain, marble and mosaic from European makers — with free samples and UK-wide delivery.",
      primaryCta: { label: "Shop now", href: "/collections" },
      secondaryCta: { label: "Order free samples", href: "/contact" },
      image: {
        url: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1800&q=80&auto=format&fit=crop",
        altText: "Modern bathroom with tiled feature wall and warm lighting",
      },
    },
    {
      id: "slide-2",
      eyebrow: "Bestsellers",
      title: "Warm terracotta, timeless bones.",
      description:
        "A curated range of clay and porcelain tiles that age with the room — quiet, tactile, built to last.",
      primaryCta: { label: "Shop terracotta", href: "/collections/terracotta" },
      secondaryCta: { label: "See all collections", href: "/collections" },
      image: {
        url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1800&q=80&auto=format&fit=crop",
        altText: "Terracotta-toned living space with layered textures",
      },
    },
    {
      id: "slide-3",
      eyebrow: "Trade & designers",
      title: "Unlock trade pricing and priority delivery.",
      description:
        "Join our trade programme for dedicated pricing, 10% designer cashback and project support from our Leeds team.",
      primaryCta: { label: "Apply for trade", href: "/contact" },
      secondaryCta: { label: "About Tilefort", href: "/about" },
      image: {
        url: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1800&q=80&auto=format&fit=crop",
        altText: "Showroom view of stacked porcelain tiles",
      },
    },
  ],
  usp: [
    {
      id: "delivery",
      title: "Express Delivery",
      description: "Fast, secure shipping you can rely on.",
      icon: "package" as const,
    },
    {
      id: "pro",
      title: "PRO Privileges",
      description: "Exclusive pricing & benefits for professionals.",
      icon: "award" as const,
    },
    {
      id: "returns",
      title: "30 Days Return",
      description: "Easy returns within 30 days of purchase.",
      icon: "undo" as const,
    },
    {
      id: "support",
      title: "24/7 Support",
      description: "Taking care of customers at every step.",
      icon: "headphones" as const,
    },
  ],
  inspiration: {
    eyebrow: "Inspiration",
    title: "Textures you can almost feel.",
    description:
      "Order up to four free samples to test tiles under the light of your own room.",
    ctaLabel: "Order free samples",
    ctaHref: "/contact",
    image: {
      url: "https://picsum.photos/seed/tilefort-inspiration/1600/700",
      altText: "Interior lifestyle featuring layered tile textures",
    },
  },
  spaces: [
    {
      id: "kitchen",
      label: "Kitchen",
      href: "/collections",
      image: { url: "https://picsum.photos/seed/tilefort-space-kitchen/600/600", altText: "Kitchen tile scene" },
    },
    {
      id: "bathroom",
      label: "Bathroom",
      href: "/collections",
      image: { url: "https://picsum.photos/seed/tilefort-space-bathroom/600/600", altText: "Bathroom tile scene" },
    },
    {
      id: "wetroom",
      label: "Wetroom",
      href: "/collections",
      image: { url: "https://picsum.photos/seed/tilefort-space-wetroom/600/600", altText: "Wetroom tile scene" },
    },
    {
      id: "outdoor",
      label: "Outdoor",
      href: "/collections/outdoor",
      image: { url: "https://picsum.photos/seed/tilefort-space-outdoor/600/600", altText: "Outdoor paver scene" },
    },
    {
      id: "utility",
      label: "Utility",
      href: "/collections",
      image: { url: "https://picsum.photos/seed/tilefort-space-utility/600/600", altText: "Utility room tile scene" },
    },
    {
      id: "hallway",
      label: "Hallway",
      href: "/collections",
      image: { url: "https://picsum.photos/seed/tilefort-space-hallway/600/600", altText: "Hallway tile scene" },
    },
    {
      id: "living",
      label: "Living room",
      href: "/collections",
      image: { url: "https://picsum.photos/seed/tilefort-space-living/600/600", altText: "Living room tile scene" },
    },
    {
      id: "commercial",
      label: "Commercial",
      href: "/collections",
      image: { url: "https://picsum.photos/seed/tilefort-space-commercial/600/600", altText: "Commercial tile scene" },
    },
  ],
  promoStrip: {
    message: "Free samples on every order · 25% off outdoor pavers until Sunday",
    ctaLabel: "Shop offers",
    ctaHref: "/collections/outdoor",
  },
  promoBanner: {
    eyebrow: "Trade & designers",
    title: "Unlock trade pricing and priority pallet delivery.",
    description:
      "Apply for a free trade account for dedicated pricing, 10% designer cashback and project support.",
    ctaLabel: "Apply for trade",
    ctaHref: "/contact",
    image: {
      url: "https://picsum.photos/seed/tilefort-promo-banner/1600/600",
      altText: "Interior with freshly laid porcelain tile floor",
    },
  },
  featureSplit: {
    title: "Your Destination for Premium Tile Solutions",
    description:
      "Transform any space with our wide-ranging tile collection — from modern backsplashes to show-stopping entrances for homes and businesses.",
    ctaLabel: "Read More",
    ctaHref: "/about",
    image: {
      url: "https://picsum.photos/seed/tilefort-feature-split/1200/1200",
      altText: "Modern bathroom interior with porcelain and marble tiles",
    },
  },
  knowledgeCenter: {
    title: "Knowledge Center",
    ctaLabel: "Read All",
    ctaHref: "/about",
    articles: [
      {
        id: "art-porcelain-vs-ceramic",
        title: "Porcelain vs. Ceramic Tile: What's the Difference?",
        excerpt:
          "Not sure which tile is right for your project? We break down the key differences in strength, water resistance, maintenance and where each shines.",
        date: "Apr 10, 2026",
        href: "/about",
        image: {
          url: "https://picsum.photos/seed/tilefort-article-porcelain/800/500",
          altText: "Side-by-side porcelain and ceramic tile samples",
        },
      },
      {
        id: "art-bathroom-ideas",
        title: "Bathroom Tile Ideas That Elevate Your Space",
        excerpt:
          "Explore timeless and on-trend bathroom tile ideas — from statement feature walls to subtle texture combinations — designed to create a spa-like retreat at home.",
        date: "Apr 02, 2026",
        href: "/about",
        image: {
          url: "https://picsum.photos/seed/tilefort-article-bathroom/800/500",
          altText: "Serene tiled bathroom with warm stone finish",
        },
      },
      {
        id: "art-size-layout",
        title: "Tile Size & Layout: Design Tips That Make a Difference",
        excerpt:
          "From classic subway patterns to modern large-format layouts, discover how tile size and placement can transform the look and feel of any room.",
        date: "Mar 21, 2026",
        href: "/about",
        image: {
          url: "https://picsum.photos/seed/tilefort-article-layout/800/500",
          altText: "Herringbone tile layout detail in a kitchen",
        },
      },
    ],
  },
  testimonials: [
    {
      id: "t-emma",
      author: "Emma Wilson",
      rating: 5,
      quote:
        "I'm extremely happy with my purchase. The tile looks even better in person. The colour is rich, the quality is outstanding, and it completely transformed my kitchen.",
      image: {
        url: "https://picsum.photos/seed/tilefort-testimonial-emma/200/150",
        altText: "Kitchen featuring warm porcelain tiles",
      },
    },
    {
      id: "t-james",
      author: "James Rodriguez",
      rating: 5,
      quote:
        "I'm very impressed with both the design and durability. The finish looks high-end and has held up perfectly so far. Ordering samples helped a lot, delivery was fast, and the tile installed perfectly.",
      image: {
        url: "https://picsum.photos/seed/tilefort-testimonial-james/200/150",
        altText: "Marble floor in entryway",
      },
    },
    {
      id: "t-sophia",
      author: "Sophia Chen",
      rating: 5,
      quote:
        "The samples made decision-making easy. What arrived matched exactly what we saw — no surprises. Top-quality materials and a great selection.",
      image: {
        url: "https://picsum.photos/seed/tilefort-testimonial-sophia/200/150",
        altText: "Bathroom with marble-effect tiles",
      },
    },
    {
      id: "t-liam",
      author: "Liam O'Connell",
      rating: 5,
      quote:
        "The tiles added so much character to our bathroom. Customer service was incredibly helpful in choosing the right style. The final result looks modern and elegant.",
      image: {
        url: "https://picsum.photos/seed/tilefort-testimonial-liam/200/150",
        altText: "Modern bathroom with patterned feature wall",
      },
    },
  ],
  testimonialsMeta: {
    title: "What Customers Say About Us",
    ratingText: "4.5/5 by 650+ Happy Customers!",
  },
  brandStrip: [
    "Casa Nobile",
    "Atelier Milano",
    "Pietra Nera",
    "Nord Tile Co.",
    "Brick & Bond",
    "Outland Stone",
    "Casa Vecchia",
    "Little Works",
  ],
  newsletter: {
    eyebrow: "Stay in the loop",
    title: "New collections, from quarry to doorstep.",
    description:
      "A slow newsletter. Seasonal drops, behind-the-scenes visits, and the odd early-bird discount.",
    inputLabel: "Email address",
    submitLabel: "Subscribe",
    disclaimer: "No spam. Unsubscribe anytime.",
  },
  topBar: [
    { id: "samples", label: "Free samples" },
    { id: "delivery", label: "UK-wide delivery" },
    { id: "trade", label: "Trade accounts" },
  ],
  primaryNav: [
    { label: "Porcelain", href: "/collections/porcelain" },
    { label: "Marble", href: "/collections/marble" },
    { label: "Metro", href: "/collections/metro" },
    { label: "Mosaic", href: "/collections/mosaic" },
    { label: "Outdoor", href: "/collections/outdoor" },
    { label: "Wood-effect", href: "/collections/wood-effect" },
    { label: "All collections", href: "/collections" },
  ],
  footerNav: {
    shop: {
      title: "Shop",
      links: [
        { label: "Porcelain", href: "/collections/porcelain" },
        { label: "Marble", href: "/collections/marble" },
        { label: "Metro", href: "/collections/metro" },
        { label: "Mosaic", href: "/collections/mosaic" },
        { label: "Outdoor", href: "/collections/outdoor" },
        { label: "All collections", href: "/collections" },
      ],
    },
    help: {
      title: "Help",
      links: [
        { label: "Free samples", href: "/contact" },
        { label: "Delivery", href: "/contact" },
        { label: "Returns", href: "/contact" },
        { label: "Care guide", href: "/contact" },
        { label: "FAQs", href: "/contact" },
      ],
    },
    company: {
      title: "Company",
      links: [
        { label: "About", href: "/about" },
        { label: "Trade accounts", href: "/contact" },
        { label: "Contact", href: "/contact" },
        { label: "Sustainability", href: "/about" },
      ],
    },
  },
  payments: ["Visa", "Mastercard", "AmEx", "PayPal", "Klarna"],
  social: [
    { label: "Instagram", href: "https://instagram.com" },
    { label: "Pinterest", href: "https://pinterest.com" },
    { label: "LinkedIn", href: "https://linkedin.com" },
  ],
} as const;

export const uspIconMap: Record<string, LucideIcon> = {
  palette: Palette,
  truck: Truck,
  ruler: Ruler,
  undo: Undo2,
  shield: ShieldCheck,
  leaf: Leaf,
  package: Package,
  award: Award,
  headphones: Headphones,
};
